// "New initiative" form defaults — most people create for the same team, pod
// and owner every time, so the form starts with their last pick.
//
// Remembered per signed-in user in this browser only (localStorage): no
// schema change, and a new device simply starts from the inferred defaults.
// Storage can be unavailable (private windows, blocked site data) — every
// access is guarded and the form just starts blank.

import type { Initiative, Person } from "./types";
import { TEAMS } from "./constants";

export type FormDefaults = { teamId: string; podId: string; driId: string };

const KEY_PREFIX = "roadmap.newInitiativeDefaults:";
const EMPTY: FormDefaults = { teamId: "", podId: "", driId: "" };

// Signed-out (auth off) sessions share one slot.
function keyFor(userId: string | null | undefined): string {
  return KEY_PREFIX + (userId || "anonymous");
}

export function loadDefaults(userId: string | null | undefined): Partial<FormDefaults> | null {
  try {
    const raw = window.localStorage.getItem(keyFor(userId));
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && typeof v === "object" ? v : null;
  } catch {
    return null;
  }
}

export function saveDefaults(userId: string | null | undefined, d: FormDefaults): void {
  try {
    window.localStorage.setItem(keyFor(userId), JSON.stringify(d));
  } catch {
    // Storage blocked or full — remembering is a convenience, never an error.
  }
}

// The team/pod pair this person most often owns initiatives in.
function inferTeamPod(userId: string, initiatives: Initiative[]): { teamId: string; podId: string } | null {
  const counts = new Map<string, number>();
  for (const i of initiatives) {
    if (i.driId !== userId || !i.teamId) continue;
    const k = `${i.teamId}|${i.podId ?? ""}`;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  let best: string | null = null;
  counts.forEach((n, k) => { if (best === null || n > (counts.get(best) ?? 0)) best = k; });
  if (best === null) return null;
  const [teamId, podId] = (best as string).split("|");
  return { teamId, podId };
}

// Team, pod and owner to start the form with: the last pick, falling back to
// what the user usually owns (team/pod) and to the user themself (owner).
// Anything that no longer exists — a retired pod, someone off the roster —
// is dropped rather than prefilled.
export function resolveDefaults(opts: {
  remembered: Partial<FormDefaults> | null;
  currentUserId: string | null | undefined;
  initiatives: Initiative[];
  roster: Person[];
}): FormDefaults {
  const { remembered, currentUserId, initiatives, roster } = opts;
  const out: FormDefaults = { ...EMPTY };

  const pick = remembered?.teamId ? { teamId: remembered.teamId, podId: remembered.podId ?? "" }
    : currentUserId ? inferTeamPod(currentUserId, initiatives)
    : null;
  const team = pick ? TEAMS.find(t => t.id === pick.teamId) : undefined;
  if (team && pick) {
    out.teamId = team.id;
    if (team.pods.some(p => p.id === pick.podId)) out.podId = pick.podId;
  }

  const owner = remembered?.driId && roster.some(p => p.id === remembered.driId)
    ? remembered.driId
    : currentUserId && roster.some(p => p.id === currentUserId) ? currentUserId : "";
  out.driId = owner;
  return out;
}
